import Splide from '@splidejs/splide';

// ── Helpers : URL d'embed YouTube / Vimeo ─────────────────────────────────
const getYoutubeId = (url) => {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  );
  return match ? match[1] : null;
};

const getVimeoId = (url) => {
  const match = url.match(/vimeo\.com\/(?:.*\/)?(\d+)/);
  return match ? match[1] : null;
};

const buildEmbedUrl = (url, { autoplay = false, loop = false, mute = false, controls = true } = {}) => {
  const youtubeId = getYoutubeId(url);
  if (youtubeId) {
    const params = new URLSearchParams({
      autoplay: autoplay ? '1' : '0',
      mute: mute ? '1' : '0',
      loop: loop ? '1' : '0',
      controls: controls ? '1' : '0',
      playsinline: '1',
      rel: '0',
      // Requis pour piloter le lecteur via postMessage (pause à distance).
      enablejsapi: '1',
    });
    if (!controls) {
      // Masque le titre / la marque YouTube et les vidéos suggérées au survol.
      params.set('modestbranding', '1');
      params.set('iv_load_policy', '3');
      params.set('disablekb', '1');
    }
    if (loop) params.set('playlist', youtubeId);
    return `https://www.youtube.com/embed/${youtubeId}?${params.toString()}`;
  }

  const vimeoId = getVimeoId(url);
  if (vimeoId) {
    const params = new URLSearchParams({
      autoplay: autoplay ? '1' : '0',
      muted: mute ? '1' : '0',
      loop: loop ? '1' : '0',
      controls: controls ? '1' : '0',
      playsinline: '1',
    });
    if (!controls) {
      // Masque le titre, l'auteur et la miniature de profil Vimeo.
      params.set('title', '0');
      params.set('byline', '0');
      params.set('portrait', '0');
    }
    return `https://player.vimeo.com/video/${vimeoId}?${params.toString()}`;
  }

  return null;
};

const createIframe = (src) => {
  const iframe = document.createElement('iframe');
  iframe.src = src;
  iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
  iframe.setAttribute('allowfullscreen', '');
  iframe.setAttribute('frameborder', '0');
  return iframe;
};

// ── Stoppe la lecture de toutes les vidéos d'un bloc, sauf celle visée ─────
const stopOtherItems = (block, currentId) => {
  block.querySelectorAll('.VideoBlock-item').forEach((item) => {
    if (item.dataset.videoId === currentId) return;

    const source = item.dataset.videoSource;

    if (source === 'file') {
      item.querySelectorAll('video').forEach((video) => video.pause());
      return;
    }

    const iframe = item.querySelector('.VideoBlock-embed iframe');
    if (!iframe) return;

    if (source === 'youtube') {
      iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
    } else if (source === 'vimeo') {
      iframe.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
    }

    item.classList.remove('is-playing');
  });
};

// ── Lecture automatique muette en boucle (YouTube / Vimeo) ────────────────
const loadAutoplayEmbed = (item, embed) => {
  const url = item.dataset.videoUrl;
  if (!url) return;

  const controls = item.dataset.videoControls === '1';
  const src = buildEmbedUrl(url, { autoplay: true, loop: true, mute: true, controls });
  if (!src) return;

  const iframe = createIframe(src);
  embed.appendChild(iframe);
  embed.dataset.loaded = '1';

  if (!controls) {
    // YouTube/Vimeo affichent une interface de lancement (titre, lecture/
    // pause...) pendant quelques secondes après le chargement, même avec
    // controls=0. On masque l'iframe jusqu'à ce qu'elle ait eu le temps de
    // disparaître.
    iframe.addEventListener('load', () => {
      setTimeout(() => {
        iframe.style.opacity = '1';
      }, 5000);
    });
  }
};

// ── Aperçu image + lecture au clic ─────────────────────────────────────────
const bindClickToPlay = (block, item, embed) => {
  const playBtn = item.querySelector('.VideoBlock-playBtn');
  if (!playBtn || !embed) return;

  playBtn.addEventListener('click', () => {
    stopOtherItems(block, item.dataset.videoId);

    if (!embed.dataset.loaded) {
      const source = item.dataset.videoSource;

      if (source === 'file' && item.dataset.videoSrc) {
        const video = document.createElement('video');
        video.src = item.dataset.videoSrc;
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
        embed.appendChild(video);
      } else if (item.dataset.videoUrl) {
        const src = buildEmbedUrl(item.dataset.videoUrl, { autoplay: true, loop: false, mute: false, controls: true });
        if (src) embed.appendChild(createIframe(src));
      }

      embed.dataset.loaded = '1';
    } else {
      const video = embed.querySelector('video');
      if (video) video.play();
    }

    item.classList.add('is-playing');
  });
};

const gk_videoBlock = {
  load() {
    document.querySelectorAll('.VideoBlock').forEach((block) => {
      if (block.dataset.gkInit) return;
      block.dataset.gkInit = '1';

      if (block.classList.contains('-modeSlider')) {
        this.initSplide(block);
      }

      block.querySelectorAll('.VideoBlock-item').forEach((item) => {
        const embed = item.querySelector('.VideoBlock-embed');
        if (!embed) return;

        if (item.dataset.videoMode === 'autoplay_muted') {
          if (item.dataset.videoSource !== 'file') {
            loadAutoplayEmbed(item, embed);
          }
        } else {
          bindClickToPlay(block, item, embed);
        }
      });
    });
  },

  initSplide(block) {
    const splideEl = block.querySelector('.VideoBlock-splide');
    if (!splideEl) return;

    const slideCount = splideEl.querySelectorAll('.splide__slide').length;
    if (slideCount < 2) return;

    const speed         = parseInt(block.dataset.speed, 10) || 750;
    const autoplay       = block.dataset.autoplay === '1';
    const autoplaySpeed  = parseInt(block.dataset.autoplaySpeed, 10) || 4000;
    const rewind         = block.dataset.rewind === '1';
    const showArrows     = block.dataset.showArrows === '1';
    const showDots       = block.dataset.showDots === '1';

    const perPageDesktopLarge = parseInt(block.dataset.perPageDesktopLarge, 10) || 3;
    const perPageDesktop      = parseInt(block.dataset.perPageDesktop, 10) || 3;
    const perPageTabletLarge  = parseInt(block.dataset.perPageTabletLarge, 10) || 2;
    const perPageTablet       = parseInt(block.dataset.perPageTablet, 10) || 2;
    const perPageMobileLarge  = parseInt(block.dataset.perPageMobileLarge, 10) || 1;
    const perPageMobile       = parseInt(block.dataset.perPageMobile, 10) || 1;

    const safeMax = (n) => Math.min(n, slideCount);

    const splide = new Splide(splideEl, {
      type:         rewind ? 'loop' : 'slide',
      perPage:      safeMax(perPageDesktopLarge),
      perMove:      1,
      speed,
      autoplay,
      interval:     autoplaySpeed,
      pauseOnHover: true,
      pauseOnFocus: true,
      arrows:       showArrows,
      pagination:   showDots,
      gap:          '24px',
      easing:       'cubic-bezier(0.76, 0, 0.24, 1)',
      keyboard:     'global',
      i18n: {
        prev:   'Vidéo précédente',
        next:   'Vidéo suivante',
        first:  'Première vidéo',
        last:   'Dernière vidéo',
        slideX: 'Vidéo %s',
        pageX:  'Page %s',
      },
      breakpoints: {
        1199: { perPage: safeMax(perPageDesktop) },
        1023: { perPage: safeMax(perPageTabletLarge) },
         990: { perPage: safeMax(perPageTablet) },
         767: { perPage: safeMax(perPageMobileLarge) },
         399: { perPage: safeMax(perPageMobile) },
      },
    });

    splide.mount();
    splideEl._splide = splide;
  },
};

export default gk_videoBlock;
